import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useProposal } from "../../contexts/ProposalContext";
import { useAuth } from "../../contexts/AuthContext";
import { apiClient } from "../../services/apiClient";

// Components
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";

const ProposalForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // For editing existing proposals
  const { user } = useAuth();
  const {
    currentProposal,
    customers,
    loading,
    error,
    fetchProposal,
    fetchCustomers,
    createProposal,
    updateProposal,
    clearError,
  } = useProposal();

  const isEditing = !!id;
  const isAdmin = user?.role === "admin";

  // Check if we have AI-extracted data from RFP upload
  const extractedData = location.state?.extractedData;
  const rfpDocumentId = location.state?.rfpDocumentId;
  const isFromRfpExtraction = location.state?.isFromRfpExtraction;

  const [formData, setFormData] = useState({
    proposal_name: extractedData?.proposal_name || "",
    project_title: extractedData?.project_title || "",
    customer_id: "",
    proposal_type: "RFP",
    status: "Active",
    description: extractedData?.description || "",
    due_date: extractedData?.due_date || "",
    contract_value: extractedData?.contract_value || "",
    rfp_number: extractedData?.rfp_number || "",
    project_keywords: extractedData?.project_keywords || "",
  });

  const [aiExtractedFields, setAiExtractedFields] = useState(new Set());
  const [customerFromExtraction, setCustomerFromExtraction] = useState(null);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersWithAccess, setUsersWithAccess] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    customer_name: "",
    customer_type: "Government",
  });

  useEffect(() => {
    fetchCustomers();
    if (isAdmin) {
      loadUsers();
    }

    if (isEditing && id) {
      fetchProposal(parseInt(id));
    }
  }, [isEditing, id, fetchCustomers, fetchProposal, isAdmin]);

  // Handle AI-extracted data initialization
  useEffect(() => {
    if (isFromRfpExtraction && extractedData) {
      // Mark which fields were AI-extracted
      const extractedFieldNames = new Set();
      Object.keys(extractedData).forEach(key => {
        if (extractedData[key] !== null && extractedData[key] !== "") {
          extractedFieldNames.add(key);
        }
      });
      setAiExtractedFields(extractedFieldNames);

      // Handle customer data from extraction
      if (extractedData.customer_name) {
        setCustomerFromExtraction({
          customer_name: extractedData.customer_name,
          customer_type: extractedData.customer_type || "Government"
        });
        setShowNewCustomerForm(true);
        setNewCustomer({
          customer_name: extractedData.customer_name,
          customer_type: extractedData.customer_type || "Government"
        });
      }
    }
  }, [isFromRfpExtraction, extractedData]);

  useEffect(() => {
    if (isEditing && currentProposal) {
      setFormData({
        proposal_name: currentProposal.proposal_name || "",
        project_title: currentProposal.project_title || "",
        customer_id: currentProposal.customer_id || "",
        proposal_type: currentProposal.proposal_type || "RFP",
        status: currentProposal.status || "Active",
        description: currentProposal.description || "",
        due_date: currentProposal.due_date
          ? new Date(currentProposal.due_date).toISOString().split("T")[0]
          : "",
        contract_value: currentProposal.contract_value || "",
        rfp_number: currentProposal.rfp_number || "",
        project_keywords: currentProposal.project_keywords || "",
      });

      // Set users with access for editing
      if (currentProposal.usersWithAccess) {
        setUsersWithAccess(currentProposal.usersWithAccess);
        const userIds = currentProposal.usersWithAccess
          .filter(
            (u) => u.access_level !== "admin" || u.user_id !== user?.user_id
          )
          .map((u) => u.user_id);
        setSelectedUsers(userIds);
      }
    }
  }, [isEditing, currentProposal, user]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await apiClient.getUsers();
      if (response.success) {
        const activeUsers = response.data.users
          .filter((user) => user.is_active)
          .sort((a, b) => a.username.localeCompare(b.username));
        setUsers(activeUsers);
      }
    } catch (err) {
      console.error("Failed to load users:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Redirect non-admins trying to create new proposals
  if (!isEditing && !isAdmin) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
          <FontAwesomeIcon
            icon="shield-alt"
            className="text-4xl text-red-300 mb-4"
          />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Admin Access Required
          </h1>
          <p className="text-slate-600 mb-6">
            Only administrators can create new proposals. Please contact your
            administrator to request access to a proposal.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Check if user has write access for editing
  if (isEditing && currentProposal && !isAdmin) {
    // Non-admins can only edit if they have write access
    const userAccess = currentProposal.userAccess;
    if (userAccess !== "admin" && userAccess !== "write") {
      return (
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
            <FontAwesomeIcon
              icon="lock"
              className="text-4xl text-red-300 mb-4"
            />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Access Denied
            </h1>
            <p className="text-slate-600 mb-6">
              You don't have permission to edit this proposal. Please contact an
              administrator if you need write access.
            </p>
            <button
              onClick={() => navigate(`/proposal-details/${id}`)}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              View Proposal Details
            </button>
          </div>
        </div>
      );
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.proposal_name.trim()) {
      errors.proposal_name = "Proposal name is required";
    } else if (formData.proposal_name.length < 5) {
      errors.proposal_name = "Proposal name must be at least 5 characters";
    }

    if (!formData.customer_id && !showNewCustomerForm) {
      errors.customer_id = "Please select a customer or create a new one";
    }

    if (showNewCustomerForm && !newCustomer.customer_name.trim()) {
      errors.new_customer_name = "Customer name is required";
    }

    // Only validate user selection for new proposals and admins
    if (!isEditing && isAdmin && selectedUsers.length === 0) {
      errors.users = "At least one user must be selected";
    }

    // Note: Removed past date restriction per user request -
    // Users should be able to create historical proposals

    if (formData.contract_value && isNaN(parseFloat(formData.contract_value))) {
      errors.contract_value = "Contract value must be a valid number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    clearError();

    try {
      let customerId = formData.customer_id;

      if (showNewCustomerForm) {
        // Check if customer already exists in the dropdown first
        const existingCustomer = customers.find(
          c => c.customer_name.toLowerCase() === newCustomer.customer_name.toLowerCase()
        );

        if (existingCustomer) {
          // Use existing customer instead of creating a new one
          console.log("Using existing customer:", existingCustomer);
          customerId = existingCustomer.customer_id;
        } else {
          // Create new customer
          console.log("Creating new customer:", newCustomer);
          try {
            const customerResponse = await apiClient.createCustomer(newCustomer);
            console.log("Customer creation response:", customerResponse);

            if (!customerResponse.success) {
              throw new Error(
                customerResponse.message || "Failed to create customer"
              );
            }

            customerId = customerResponse.data.customerId;
          } catch (error) {
            // If customer creation fails due to duplicate, try to find it in the list
            if (error.response?.status === 400 && error.response?.data?.message?.includes("already exists")) {
              console.log("Customer already exists, trying to find in list...");
              // Refresh customers list and try to find the customer
              await fetchCustomers();
              const foundCustomer = customers.find(
                c => c.customer_name.toLowerCase() === newCustomer.customer_name.toLowerCase()
              );
              if (foundCustomer) {
                customerId = foundCustomer.customer_id;
              } else {
                throw new Error("Customer creation failed and could not be found in existing customers");
              }
            } else {
              throw error;
            }
          }
        }
      }

      const proposalData = {
        ...formData,
        customer_id: customerId,
        contract_value: formData.contract_value
          ? parseFloat(formData.contract_value)
          : null,
      };

      // Add selected users for new proposals or when admin is editing
      if ((!isEditing || isAdmin) && selectedUsers.length > 0) {
        proposalData.selectedUsers = selectedUsers;
      }

      let result;
      if (isEditing) {
        result = await updateProposal(parseInt(id), proposalData);
      } else if (isFromRfpExtraction && rfpDocumentId) {
        // Use the specialized endpoint for RFP-extracted proposals
        const response = await apiClient.post('/api/proposals/create-from-extraction', {
          extractedData,
          rfpDocumentId,
          finalData: proposalData
        });
        console.log("Create from extraction response:", response.data);
        result = response.data;
      } else {
        result = await createProposal(proposalData);
      }

      // Handle different response structures
      let isSuccess = result.success || result.data?.success;
      let proposalId;

      if (isEditing) {
        proposalId = parseInt(id);
      } else if (isFromRfpExtraction) {
        // For RFP extraction, the response might be structured differently
        proposalId = result.proposalId || result.data?.proposalId || result.data?.data?.proposalId;
        // If we have a proposalId from RFP extraction, consider it successful even without explicit success flag
        if (proposalId && !isSuccess) {
          isSuccess = true;
        }
      } else {
        proposalId = result.data?.proposalId;
      }

      console.log("Navigation check:", { isSuccess, proposalId, isFromRfpExtraction, result });

      if (isSuccess && proposalId) {
        if (isFromRfpExtraction) {
          // Navigate to workspace for RFP-created proposals as requested
          console.log(`Navigating to workspace: /workspace/${proposalId}`);
          navigate(`/workspace/${proposalId}`);
        } else {
          navigate(`/proposal-details/${proposalId}`);
        }
      } else {
        console.error("Navigation failed - missing success or proposalId:", { isSuccess, proposalId, result });
      }
    } catch (error) {
      console.error("Error saving proposal:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      navigate(`/proposal-details/${id}`);
    } else {
      navigate("/dashboard");
    }
  };

  // Helper function to determine if a field was AI-extracted
  const isAiExtracted = (fieldName) => {
    return aiExtractedFields.has(fieldName);
  };

  // Helper function to get AI indicator styling
  const getAiIndicatorStyle = (fieldName) => {
    if (isAiExtracted(fieldName)) {
      return "border-l-4 border-l-blue-400 bg-blue-50";
    }
    return "";
  };

  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading proposal..." />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center space-x-3 mb-2">
          <FontAwesomeIcon
            icon={isEditing ? "edit" : isFromRfpExtraction ? "robot" : "plus"}
            className="text-primary-500"
          />
          <h1 className="text-2xl font-bold text-slate-900">
            {isEditing ? "Edit Proposal" : isFromRfpExtraction ? "Review AI-Extracted Proposal" : "Create New Proposal"}
          </h1>
        </div>
        <p className="text-slate-600">
          {isEditing
            ? "Update the proposal details below."
            : isFromRfpExtraction
            ? "Review and edit the AI-extracted information before creating the proposal."
            : "Fill in the details to create a new proposal or RFP response."}
        </p>
        {isFromRfpExtraction && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon="info-circle" className="text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                Fields with blue highlighting were extracted by AI from your RFP document
              </span>
            </div>
          </div>
        )}
      </div>

      {error && <ErrorMessage message={error} onRetry={clearError} />}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left/Middle Columns - Proposal Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Basic Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Proposal Name <span className="text-red-500">*</span>
                  </label>
                  <div className={`relative ${getAiIndicatorStyle("proposal_name")}`}>
                    <input
                      type="text"
                      name="proposal_name"
                      value={formData.proposal_name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.proposal_name
                          ? "border-red-300"
                          : "border-slate-300"
                      } ${isAiExtracted("proposal_name") ? "pr-8" : ""}`}
                      placeholder="Enter a descriptive name for this proposal"
                    />
                    {isAiExtracted("proposal_name") && (
                      <FontAwesomeIcon
                        icon="robot"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-500 text-sm"
                        title="AI-extracted from RFP document"
                      />
                    )}
                  </div>
                  {formErrors.proposal_name && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.proposal_name}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Project Title
                  </label>
                  <input
                    type="text"
                    name="project_title"
                    value={formData.project_title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Official project or RFP title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Proposal Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="proposal_type"
                    value={formData.proposal_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="RFP">Active RFP</option>
                    <option value="Past_Proposal">Past Proposal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                    <option value="Archived">Archived</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">
                    Active: Currently working on • Completed: Submitted/Won •
                    Archived: Historical reference
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    RFP/Solicitation Number
                  </label>
                  <input
                    type="text"
                    name="rfp_number"
                    value={formData.rfp_number}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., W911QX-25-R-0001"
                  />
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">
                  Customer Information
                </h2>
                <button
                  type="button"
                  onClick={() => setShowNewCustomerForm(!showNewCustomerForm)}
                  className="px-3 py-1 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md transition-colors duration-200"
                >
                  {showNewCustomerForm
                    ? "Select Existing"
                    : "Create New Customer"}
                </button>
              </div>

              {showNewCustomerForm ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Customer Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="customer_name"
                      value={newCustomer.customer_name}
                      onChange={handleNewCustomerChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.new_customer_name
                          ? "border-red-300"
                          : "border-slate-300"
                      }`}
                      placeholder="Enter customer organization name"
                    />
                    {formErrors.new_customer_name && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.new_customer_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Customer Type
                    </label>
                    <select
                      name="customer_type"
                      value={newCustomer.customer_type}
                      onChange={handleNewCustomerChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="Government">Government</option>
                      <option value="Private">Private</option>
                      <option value="Non-Profit">Non-Profit</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select Customer <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="customer_id"
                    value={formData.customer_id}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.customer_id
                        ? "border-red-300"
                        : "border-slate-300"
                    }`}
                  >
                    <option value="">Select a customer...</option>
                    {customers.map((customer) => (
                      <option
                        key={customer.customer_id}
                        value={customer.customer_id}
                      >
                        {customer.customer_name} ({customer.customer_type})
                      </option>
                    ))}
                  </select>
                  {formErrors.customer_id && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.customer_id}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Project Details */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Project Details
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Brief description of the proposal or project requirements..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Due Date
                    </label>
                    <input
                      type="date"
                      name="due_date"
                      value={formData.due_date}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.due_date
                          ? "border-red-300"
                          : "border-slate-300"
                      }`}
                    />
                    {formErrors.due_date && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.due_date}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Contract Value ($)
                    </label>
                    <input
                      type="number"
                      name="contract_value"
                      value={formData.contract_value}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.contract_value
                          ? "border-red-300"
                          : "border-slate-300"
                      }`}
                      placeholder="Enter estimated contract value"
                      min="0"
                      step="0.01"
                    />
                    {formErrors.contract_value && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.contract_value}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Project Keywords
                  </label>
                  <input
                    type="text"
                    name="project_keywords"
                    value={formData.project_keywords}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., software development, cybersecurity, training, logistics"
                  />
                  <p className="mt-1 text-sm text-slate-500">
                    Comma-separated keywords to help with searching and
                    categorization
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - User Access Management (Admin Only) */}
          {isAdmin && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sticky top-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">
                    User Access{" "}
                    {!isEditing && <span className="text-red-500">*</span>}
                  </h2>
                  <div className="text-sm text-slate-500">
                    {selectedUsers.length} selected
                  </div>
                </div>

                {loadingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner size="sm" text="Loading users..." />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Current Access Display (for editing) */}
                    {isEditing && usersWithAccess.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-slate-700 mb-2">
                          Current Access:
                        </h4>
                        <div className="space-y-1 text-xs">
                          {usersWithAccess.map((user) => (
                            <div
                              key={user.user_id}
                              className="flex items-center justify-between bg-slate-50 p-2 rounded"
                            >
                              <span className="font-medium">
                                {user.username}
                              </span>
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  user.access_level === "admin"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-blue-100 text-blue-700"
                                }`}
                              >
                                {user.access_level}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
                      {users.length === 0 ? (
                        <div className="p-4 text-center text-slate-500 text-sm">
                          No active users found
                        </div>
                      ) : (
                        <div className="p-3 space-y-2">
                          {users.map((user) => (
                            <label
                              key={user.user_id}
                              className="flex items-center p-2 rounded hover:bg-slate-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.user_id)}
                                onChange={() => handleUserToggle(user.user_id)}
                                className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="text-sm font-medium text-slate-900">
                                      {user.username}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      {user.email}
                                    </div>
                                  </div>
                                  <span
                                    className={`px-2 py-1 text-xs rounded-full ${
                                      user.role === "admin"
                                        ? "bg-red-100 text-red-700"
                                        : "bg-blue-100 text-blue-700"
                                    }`}
                                  >
                                    {user.role}
                                  </span>
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {formErrors.users && (
                      <p className="text-red-500 text-sm mt-2">
                        {formErrors.users}
                      </p>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                      <div className="text-sm text-blue-700">
                        <p className="font-medium mb-1">Access Notes:</p>
                        <ul className="text-xs space-y-1">
                          <li>
                            • Selected users can view and work with this
                            proposal
                          </li>
                          <li>• You automatically get admin access</li>
                          <li>• Access can be modified later</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              {isEditing
                ? "Changes will be saved to the existing proposal."
                : "Once created, you can upload documents and start collaborating."}
            </div>

            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                disabled={isSubmitting}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon="spinner" className="animate-spin" />
                    <span>{isEditing ? "Updating..." : "Creating..."}</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={isEditing ? "check" : "plus"} />
                    <span>
                      {isEditing ? "Update Proposal" : "Create Proposal"}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProposalForm;
